import React from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { objectLength } from "@/utils/helperFunctions";
import { isValidEmail } from "@/utils/userInformation";
import EssealPhoneInput from "@/components/EssealPhoneInput";

interface UserFormFieldsProps {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    primaryPhone: string;
  };
  index?: number;
  existingEmails?: string[];
  onChange: (key: string, value: string) => void;
}

const UserFormFields: React.FC<UserFormFieldsProps> = ({
  user,
  index,
  existingEmails = [],
  onChange,
}) => {
  const idSuffix = index !== undefined ? `-${index}` : "";

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          autoComplete="given-name"
          name="firstName"
          required
          fullWidth
          id={`firstName${idSuffix}`}
          label="First Name"
          defaultValue={user.firstName}
          onChange={(e) => onChange("firstName", e.target.value)}
          error={user.firstName.length === 0}
          helperText={user.firstName.length === 0 ? "First Name Required" : ""}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          required
          fullWidth
          id={`lastName${idSuffix}`}
          label="Last Name"
          name="lastName"
          autoComplete="family-name"
          defaultValue={user.lastName}
          onChange={(e) => onChange("lastName", e.target.value)}
          error={user.lastName.length === 0}
          helperText={user.lastName.length === 0 ? "Last Name Required" : ""}
        />
      </Grid>
      <Grid size={12}>
        {objectLength(existingEmails) > 0 ? (
          <Autocomplete
            fullWidth
            disablePortal
            options={existingEmails}
            defaultValue={user.email}
            renderInput={(params) => <TextField {...params} label="Select/Enter email" />}
            filterOptions={(options, params) => {
              const filtered = options.filter((option) =>
                option.toLowerCase().includes(params.inputValue.toLowerCase())
              );

              if (params.inputValue !== "" && !options.includes(params.inputValue)) {
                filtered.push(params.inputValue);
              }

              return filtered;
            }}
            onChange={(_, value) => value && onChange("email", value)}
          />
        ) : (
          <TextField
            required
            fullWidth
            id={`email${idSuffix}`}
            label="Email Address"
            name="email"
            autoComplete="email"
            defaultValue={user.email}
            onChange={(e) => onChange("email", e.target.value)}
            error={user.email.length === 0 || !isValidEmail(user.email)}
            helperText={
              user.email.length === 0
                ? "Email Required"
                : !isValidEmail(user.email)
                ? "Invalid Email"
                : ""
            }
          />
        )}
      </Grid>
      <Grid size={12}>
        <EssealPhoneInput
          id={`phone${idSuffix}`}
          label="Phone"
          name="phone"
          value={user.primaryPhone}
          onChange={(e164) => onChange("primaryPhone", e164)}
          defaultCountry="GB"
          style={{ width: "100%" }}
        />
      </Grid>
    </Grid>
  );
};

export default UserFormFields;
